<template>
  <span class="text-s font-regular text-grey-body">
    <span v-for="(part, index) in messageParts" :key="index">
      <template v-if="part.type === 'text'">{{ part.value }}</template>
      <router-link
        v-else
        class="font-medium underline text-grey-clickable focus-visible:outline-2 focus-visible:outline-dotted focus-visible:outline-primary"
        :to="part.href"
        >{{ part.label }}</router-link
      >
    </span>
  </span>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import type { ProviderAdminMessageData } from '@/apps/messages/helpers/adminMessageFormatter';

type MessagePart = { type: 'text'; value: string } | { type: 'link'; href: string; label: string };

function buildMessageParts(data: ProviderAdminMessageData): MessagePart[] {
  const parts: MessagePart[] = [];
  const text = (value: string) => parts.push({ type: 'text', value });
  const link = (href: string, label: string) => parts.push({ type: 'link', href, label });

  switch (data.type) {
    case 'job_application_replied_sitter':
      text(`${data.otherUserName} replied to your `);
      if (data.jobId) {
        link(`/provider/jobs/details/${data.jobId}`, 'job');
        text(' application');
      } else {
        text('job application');
      }
      break;
    case 'interview_request_received':
      text(`${data.otherUserName} requested `);
      if (data.kind === 'booking') {
        link(`/requests/${data.interviewId}`, 'a booking with you');
      } else {
        link(`/requests/${data.interviewId}`, 'an interview with you');
      }
      break;
    case 'interview_contract_accepted_sitter':
      text(`You accepted ${data.otherUserName}'s `);
      link(`/provider/bookings/${data.contractId}`, data.kind === 'booking' ? 'booking request' : 'interview request');
      break;
    case 'interview_declined_sitter':
      text(
        data.kind === 'booking'
          ? `You declined ${data.otherUserName}'s booking request`
          : `You declined ${data.otherUserName}'s interview request`
      );
      break;
    case 'interview_canceled_sitter':
      text(
        data.kind === 'booking'
          ? `${data.otherUserName} cancelled the booking request`
          : `${data.otherUserName} cancelled the interview request`
      );
      break;
    case 'parent_contract_cancelation_sitter': {
      const msg =
        data.kind === 'booking' ? `${data.otherUserName} cancelled the booking` : `${data.otherUserName} cancelled the interview`;
      text(msg);
      break;
    }
    case 'sitter_contract_cancelation_sitter':
      text(data.kind === 'booking' ? 'You cancelled the booking' : 'You cancelled the interview');
      break;
    case 'job_shared_sitter':
      text(`${data.otherUserName} shared `);
      link(`/provider/jobs/details/${data.jobId}`, 'their job');
      break;
    default:
      break;
  }
  return parts;
}

export default {
  name: 'MessageBubbleAdminProvider',
  props: {
    data: {
      type: Object as PropType<ProviderAdminMessageData>,
      required: true,
    },
  },
  computed: {
    messageParts(): MessagePart[] {
      return buildMessageParts(this.data);
    },
  },
};
</script>
